import CloseIcon from '@material-ui/icons/Close';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Button, FormControlLabel, Menu, MenuItem, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import loadingFileIcon from '../../../assets/icons/loadingFile.svg';
import { ButtonOne } from '../../../components/ButtonOne/ButtonOne.component';
import { CheckboxOne } from '../../../components/CheckboxOne/CheckboxOne.component';
import FileInput from '../../../components/FileInput.component';
import { InfoBox } from '../../../components/InfoBox/InfoBox.component';
import { ModalWithButtons } from '../../../components/ModalWithButtons/ModalWithButtons.component';
import { LeavePrompt } from '../../../components/Prompt/LeavePrompt/LeavePrompt.component';
import { useStyles as specialCaseUseStyles } from '../../../components/Solution/Menu/SpecialCase.styles';
import { examinersTeamImportJson } from '../../../const/exampleFile';
import { useLayoutContext } from '../../../Context/Layout.context';
import { ImportExaminerStructureRequest, ImportExaminerStructureStatus } from '../../../models/importExaminer.types';
import { checkIfAssessmentStarted } from '../../../services/distribution/distribution.service';
import {
  importExaminerStructure,
  importExaminerStructureOKE,
} from '../../../services/exams/examSheet/examSheet.service';
import { useStyles } from './ImportTeams.styles';
import { Prompt } from '../../../components/Prompt/Prompt.component';
import useSessionUser from '../../../hooks/useSessionUser';

interface ImportTeamsProps {
  sheetId: string;
  examTaskGroupIds: string[];
  onSuccess(): void;
  assessmentSessionName?: string;
  isExamSheetConfigured: boolean;
}

interface UploadedFile {
  isFileSyntaxVerified: boolean;
  validationError?: string;
  file: File;
  JSON?: ImportExaminerStructureRequest;
}

const exampleFile = examinersTeamImportJson;

export const ImportTeams: React.FC<ImportTeamsProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { user } = useSessionUser();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [confirmCancelPrompt, setConfirmCancelPrompt] = useState<boolean>(false);
  const [clearExistingTeam, setClearExistingTeam] = useState<boolean>(false);
  const [teamFile, setTeamFile] = useState<UploadedFile | undefined>();
  const [isAssessmentProcessAlreadyInProgress, setAssessmentProcessAlreadyInProgress] = useState<boolean>(false);
  const { openAlert, showLoader } = useLayoutContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [isImportOKE, setIsImportOKE] = useState<boolean>(false);
  const [isImportOKEsuccess, setIsImportOKEsucess] = useState<boolean>(false);
  const [isImportOKEerror, setIsImportOKEerror] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>();

  const hideModal = () => {
    setModalOpen(false);
  };

  const onLeave = () => {
    isImportOKE || (!isImportOKE && teamFile !== undefined) ? setConfirmCancelPrompt(true) : hideModal();
  };

  const removeFile = () => {
    setTeamFile(undefined);
  };

  const handleFileLoaded = (files: FileList) => {
    if (files.length > 0 && files[0].type === 'application/json') {
      const file = files[0];
      file.text().then((json: string) => {
        try {
          const transformedJSON: ImportExaminerStructureRequest = JSON.parse(json, function (key, value) {
            if (key === 'E') {
              this.pesel = value;
              return;
            }
            if (key === 'PZE') {
              this.chairman = { pesel: value };
              return;
            }
            if (this.chairman && (key === 'nr_egz' || key === 'number')) {
              this.chairman = { pesel: this.chairman.pesel, number: value };
              return;
            }
            if (key === 'nr_egz') {
              this.number = value;
              return;
            }
            if (key === 'Egzaminatorzy') {
              this.examiners = value;
              return;
            }
            if (key === 'nazwaGZ') {
              this.examTaskGroupName = value;
              return;
            }
            if (key === 'ZE') {
              this.examinerStructures = value;
              return;
            }
            if (key === 'kod_arkusza') {
              this.examSheetCode = value;
              return;
            }
            if (key === 'GZ') {
              this.examTaskGroupStructures = value;
              return;
            }
            return value;
          });
          setTeamFile({ isFileSyntaxVerified: true, file: file, JSON: transformedJSON });
        } catch (error) {
          console.error(error);
          setTeamFile({
            isFileSyntaxVerified: false,
            file: file,
            validationError:
              error instanceof SyntaxError ? 'Niepoprawny format pliku.' : 'Wystąpił błąd podczas ładowania.',
          });
        }
      });
    }
  };

  const setFileValidationError = (errorMessage?: string) => {
    setTeamFile((currentValue) =>
      !currentValue
        ? currentValue
        : { ...currentValue, validationError: errorMessage ? errorMessage : 'Wystąpił błąd podczas ładowania.' },
    );
  };

  const handleOnConfirm = () => {
    importExaminerStructure(props.sheetId, clearExistingTeam, teamFile?.JSON)
      .then((response) => {
        let validationErrorMessage = undefined;
        switch (response.data.status) {
          case ImportExaminerStructureStatus.Success:
            removeFile();
            setAssessmentProcessAlreadyInProgress(false);
            props.onSuccess();
            hideModal();
            openAlert('success');
            break;

          case ImportExaminerStructureStatus.SheetNotConfigured:
            validationErrorMessage = `Arkusz ${teamFile?.JSON?.examSheetCode} nie został skonfigurowany.`;
            break;

          case ImportExaminerStructureStatus.ExaminersAlreadyStartedAssessment:
            validationErrorMessage = `Nie można usunąć wcześniej zaimportowanych danych, ponieważ przynajmniej jeden egzaminator rozpoczął już ocenianie RGZ.`;
            break;

          case ImportExaminerStructureStatus.NoExaminerStructure:
            validationErrorMessage = `Brak struktury egzaminatorów.`;
            break;

          case ImportExaminerStructureStatus.ExamSheetCodeNotExist:
            validationErrorMessage = `Brak w systemie arkusza egzaminacyjnego o podanym kodzie ${teamFile?.JSON?.examSheetCode}.`;
            break;

          case ImportExaminerStructureStatus.ExamTaskGroupsNotExist:
            validationErrorMessage = `W danym arkuszu ${teamFile?.JSON?.examSheetCode}
            brak GZ ${response.data.examTaskGroups.join(', ')}.`;
            break;

          case ImportExaminerStructureStatus.ExaminersNotExists:
            validationErrorMessage = `Egzaminatorzy o podanych nr PESEL: ${response.data.examiners.join(
              ', ',
            )} nie mają jeszcze kont w systemie.`;
            break;

          case ImportExaminerStructureStatus.ExaminersWithoutNumber:
            validationErrorMessage = `Dla egzaminatorów o nr PESEL: ${response.data.examiners.join(
              ', ',
            )} nie podano nr egzaminatora.`;
            break;

          case ImportExaminerStructureStatus.ExamTaskGroupExaminersDuplicated:
            validationErrorMessage = `W GZ ${response.data.examTaskGroups.join(
              ', ',
            )} egzaminatorzy o nr PESEL: ${response.data.examiners.join(', ')} występują więcej niż jeden raz.`;
            break;

          case ImportExaminerStructureStatus.ExaminersFromAnotherCommittee:
            validationErrorMessage = `Egzaminatorzy o nr PESEL: ${response.data.examiners.join(
              ', ',
            )} są egzaminatorami z innej/innych oke.`;
            break;

          case ImportExaminerStructureStatus.CommitteeExamTaskGroupCoordinatorNotExist:
            validationErrorMessage = `Brak funkcji Koordynatora Grupy Zadań w jednej z grup zadań.`;
            break;

          case ImportExaminerStructureStatus.AnotherUserRunImport:
            validationErrorMessage = `Dla wskazanego arkusza trwa obecnie import zespołów egzaminatorów uruchomiony przez innego użytkownika systemu ${t('global:shortNames.system')}. Sprawdź aktualną strukturę egzaminatorów.`;
            break;

          case ImportExaminerStructureStatus.EmptyRequest:
          case ImportExaminerStructureStatus.Error:
          default:
            setFileValidationError();
            break;
        }
        setFileValidationError(validationErrorMessage);
      })
      .catch((error) => {
        if (!navigator.onLine) {
          openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
        }
        console.error(error);
        setFileValidationError();
      });
  };
  const handleOnConfirmOKE = () => {
    const userCommitteeId = user.profile.oke_id;
    showLoader(true);
    importExaminerStructureOKE(props.sheetId, clearExistingTeam, userCommitteeId)
      .then((response) => {
        showLoader(false);
        setAssessmentProcessAlreadyInProgress(false);
        hideModal();
        setIsImportOKEsucess(true);
      })
      .catch((error) => {
        let validationErrorMessage = undefined;
        if (!navigator.onLine) {
          openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
        } else {
          switch (error.response.data.status) {
            case ImportExaminerStructureStatus.SheetNotConfigured:
              validationErrorMessage = `Arkusz ${props.assessmentSessionName} nie został skonfigurowany.`;
              break;

            case ImportExaminerStructureStatus.ExaminersAlreadyStartedAssessment:
              validationErrorMessage = `Nie można usunąć wcześniej zaimportowanych danych, ponieważ przynajmniej jeden egzaminator rozpoczął już ocenianie RGZ.`;
              break;

            case ImportExaminerStructureStatus.NoExaminerStructure:
              validationErrorMessage = `Brak struktury egzaminatorów.`;
              break;

            case ImportExaminerStructureStatus.ExamSheetCodeNotExist:
              validationErrorMessage = `Brak w systemie arkusza egzaminacyjnego o podanym kodzie ${props.assessmentSessionName}.`;
              break;

            case ImportExaminerStructureStatus.ExamTaskGroupsNotExist:
              validationErrorMessage = `W danym arkuszu ${
                props.assessmentSessionName
              } brak GZ ${error.response.data.examTaskGroups.join(', ')}.`;
              break;

            case ImportExaminerStructureStatus.ExaminersNotExists:
              validationErrorMessage = `Egzaminatorzy o podanych nr PESEL: ${error.response.data.examiners.join(
                ', ',
              )} nie mają jeszcze kont w systemie.`;
              break;

            case ImportExaminerStructureStatus.ExaminersWithoutNumber:
              validationErrorMessage = `Dla egzaminatorów o nr PESEL: ${error.response.data.examiners.join(
                ', ',
              )} nie podano nr egzaminatora.`;
              break;

            case ImportExaminerStructureStatus.ExamTaskGroupExaminersDuplicated:
              validationErrorMessage = `W GZ ${error.response.data.examTaskGroups.join(
                ', ',
              )} egzaminatorzy o nr PESEL: ${error.response.data.examiners.join(', ')} występują więcej niż jeden raz.`;
              break;

            case ImportExaminerStructureStatus.ExaminersFromAnotherCommittee:
              validationErrorMessage = `Egzaminatorzy o nr PESEL: ${error.response.data.examiners.join(
                ', ',
              )} są egzaminatorami z innej/innych oke.`;
              break;

            case ImportExaminerStructureStatus.CommitteeExamTaskGroupCoordinatorNotExist:
              validationErrorMessage = `Brak funkcji Koordynatora Grupy Zadań w jednej z grup zadań.`;
              break;

            case ImportExaminerStructureStatus.AnotherUserRunImport:
              validationErrorMessage = `Dla wskazanego arkusza trwa obecnie import zespołów egzaminatorów uruchomiony przez innego użytkownika systemu {t('global:shortNames.system')}. Sprawdź aktualną strukturę egzaminatorów.`;
              break;

            case ImportExaminerStructureStatus.EmptyRequest:
            case ImportExaminerStructureStatus.Error:
            default:
              setValidationError('Wystąpił błąd podczas ładowania.');
              break;
          }
        }

        showLoader(false);
        setValidationError(validationErrorMessage);
      });
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <div>
        <ButtonOne
          startIcon={<UploadFileIcon />}
          variant="contained"
          color="primary"
          style={{ height: 'fit-content', margin: '0px 20px' }}
          onClick={handleClick}
          disabled={!props.isExamSheetConfigured}
        >
          {t('examinersTreemap:header.importTeamsButton')}
        </ButtonOne>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem
            disableRipple
            onClick={() => {
              setIsImportOKE(false);
              setModalOpen(true);
              handleClose();
            }}
          >
            Import z pliku
          </MenuItem>
          <MenuItem
            disableRipple
            onClick={() => {
              setIsImportOKE(true);
              setModalOpen(true); //TODO open proper modal
              handleClose();
              setValidationError(undefined);
            }}
          >
            {`Import przez ${t('global:shortNames.OKE')} API`}
          </MenuItem>
        </Menu>
      </div>
      <ModalWithButtons
        open={modalOpen}
        title="Import Zespołów"
        autoHeight
        xCloseButton={onLeave}
        onCancel={onLeave}
        onConfirm={isImportOKE ? handleOnConfirmOKE : handleOnConfirm}
        confirmButtonLabel={isImportOKE ? 'Importuj' : 'Zapisz'}
        confirmButtonDisabled={
          !isImportOKE
            ? teamFile === undefined || !teamFile.isFileSyntaxVerified || teamFile.validationError !== undefined
            : false
        }
      >
        <Grid
          container
          justifyContent="center"
          alignContent="center"
          style={{ minHeight: '500px', textAlign: 'center' }}
          spacing={2}
        >
          <Grid item xs={12}>
            <Typography variant="h6">Zaimportuj Zespoły Egzaminatorów</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <CheckboxOne
                  name="clear"
                  checked={clearExistingTeam}
                  disabled={isAssessmentProcessAlreadyInProgress}
                  onChange={() => {
                    if (!isAssessmentProcessAlreadyInProgress) {
                      checkIfAssessmentStarted(props.examTaskGroupIds).then((result) => {
                        setAssessmentProcessAlreadyInProgress(result.data.examinersStartedAssessment);
                        if (result.data.examinersStartedAssessment) setClearExistingTeam(false);
                        else setClearExistingTeam(!clearExistingTeam);
                      });
                    }
                  }}
                />
              }
              label={
                <div
                  className={classes.checkboxLabel}
                  style={isAssessmentProcessAlreadyInProgress ? { color: '#2C2D3E', opacity: '0.8' } : undefined}
                >
                  Usuń wszystkie zespoły egzaminacyjne w sesji
                </div>
              }
            />
          </Grid>
          {isAssessmentProcessAlreadyInProgress && (
            <Grid item xs={7}>
              <InfoBox
                type={'error'}
                message={
                  'Nie można usunąć wcześniej zaimportowanych danych, ponieważ przynajmniej jeden egzaminator rozpoczął już ocenianie RGZ.'
                }
              />
            </Grid>
          )}
          {!isImportOKE && (
            <Grid item xs={7}>
              <FileInput
                onFilesLoaded={handleFileLoaded}
                hint="Zaimportowane pliki powinny mieć format json, zobacz przykładowy plik"
                acceptedFileExtensions={['json']}
                exampleFile={exampleFile}
              />
            </Grid>
          )}
          {teamFile !== undefined && !isImportOKE && (
            <Grid item xs={7}>
              <Grid container alignItems="center">
                <Grid item xs={1}>
                  {teamFile.validationError === undefined ? (
                    <img src={loadingFileIcon} alt="ikona pobierania pliku" />
                  ) : teamFile.isFileSyntaxVerified && teamFile.validationError === undefined ? (
                    <CheckCircleIcon htmlColor="#347F42" />
                  ) : (
                    <AddCircleIcon htmlColor="#BC0000" style={{ transform: 'rotate(45deg)' }} />
                  )}
                </Grid>
                <Grid item xs={10} style={{ textAlign: 'left' }}>
                  <Typography>{teamFile.file.name}</Typography>
                </Grid>
                <Grid item xs={1}>
                  <Button
                    onClick={removeFile}
                    style={{
                      minWidth: 'unset',
                      color: 'rgba(44, 45, 62, 0.8)',
                    }}
                  >
                    <CloseIcon />
                  </Button>
                </Grid>
              </Grid>
              {teamFile.validationError && <div className={classes.validationError}>{teamFile.validationError}</div>}
            </Grid>
          )}
          {isImportOKE && validationError && (
            <Grid xs={12} container justifyContent="center" alignContent="center">
              <Grid xs={12}>
                <AddCircleIcon htmlColor="#BC0000" style={{ transform: 'rotate(45deg)' }} />
              </Grid>
              <Grid xs={12} container justifyContent="center" alignContent="center">
                <div className={classes.validationError}>{validationError}</div>
              </Grid>
            </Grid>
          )}
        </Grid>
      </ModalWithButtons>
      <Prompt
        type={'success'}
        open={isImportOKEsuccess}
        size="sm"
        mainActionButtonText={'OK'}
        onConfirm={() => {
          props.onSuccess();
          setIsImportOKEsucess(false);
        }}
        mainText={`Dane zostały pomyślnie zaimportowane`}
      />
      <Prompt
        type={'error'}
        open={isImportOKEerror}
        size="sm"
        mainActionButtonText={'POWRÓT'}
        onConfirm={() => setIsImportOKEerror(false)}
        mainText={`Wystąpił błąd podczas ładowania: kod błędu`}
      />
      <LeavePrompt
        isOpen={confirmCancelPrompt}
        onConfirm={() => {
          setTeamFile(undefined);
          setClearExistingTeam(false);
          setAssessmentProcessAlreadyInProgress(false);
          setConfirmCancelPrompt(false);
          hideModal();
        }}
        onCancel={() => setConfirmCancelPrompt(false)}
      />
    </>
  );
};
